import React, { useState, useEffect } from 'react';
import { Box, Autocomplete, TextField, Typography } from '@mui/material';
import FrameFatigueHandler from 'handlers/frame-fatigue.handler';
import dayjs from 'dayjs';
import AssetDetailDashboard from './Components/AssetDetailDashboard';
import FatigueDateRangeDiv from './Components/FatigueDateRangeDiv';
import { useAppSelector } from 'store/hook';
import { isDarkTheme } from 'utils/theme';
import { SingleFadeLoader } from 'components/SingleFadeLoader';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const CriticalAssets = () => {
  const { t } = useTranslation();
  const [assets, setAssets] = useState<any[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<any>(null);
  const [detailData, setDetailData] = useState<any>(null);

  const theme = useAppSelector((state) => state.authReducer).customer.theme;
  const enableDarkTheme = isDarkTheme(theme);
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const [isSelected, setIsSelected] = useState('Custom');
  const [startDate, setStartDate] = useState<any>(dayjs('2026-01-01').toDate());
  const [endDate, setEndDate] = useState<any>(dayjs('2026-01-31').toDate());
  const [aggregation, setAggregation] = useState<string>('Daily');
  const [isLoading, setIsLoading] = useState(false);

  const [selectedTimelineDate, setSelectedTimelineDate] = useState<
    string | null
  >(null);
  const handler = new FrameFatigueHandler();

  const fetchAssets = async (sD: Date, eD: Date, bumperId?: string) => {
    try {
      setIsLoading(true);
      const startStr = dayjs(sD).format('YYYY-MM-DD');
      const endStr = dayjs(eD).format('YYYY-MM-DD');
      const result = await handler.getCriticalAssets(
        startStr,
        endStr,
        bumperId
      );
      if (result && result.length > 0) {
        setAssets(result);
        // Force select first asset if none selected or if current not in new list
        if (
          !selectedAsset ||
          !result.find((a: any) => a.asset_id === selectedAsset.asset_id)
        ) {
          setSelectedAsset(result[0]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch critical assets', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (startDate && endDate) {
      void fetchAssets(startDate, endDate, selectedAsset?.asset_id);
    }
  }, [startDate, endDate]);

  const handleDateChange = (range: any) => {
    const [sD, eD] = range;
    setStartDate(sD);
    setEndDate(eD);
    setSelectedTimelineDate(null); // Reset local filter when global range changes
  };

  // 2. Fetch details for the selected truck
  useEffect(() => {
    if (selectedAsset) {
      const fetchDetail = async () => {
        try {
          setIsLoading(true);
          if (!dayjs(startDate).isValid() || !dayjs(endDate).isValid()) {
            setIsLoading(false);
            return;
          }
          const startStr = dayjs(startDate).format('YYYY-MM-DD');
          const endStr = dayjs(endDate).format('YYYY-MM-DD');
          const result = await handler.getAssetDetail(
            selectedAsset.asset_id,
            startStr,
            endStr,
            selectedTimelineDate ?? undefined
          );
          if (result) {
            setDetailData(result);
          }
        } catch (error) {
          console.error('Failed to fetch asset detail', error);
        } finally {
          setIsLoading(false);
        }
      };
      void fetchDetail();
    }
  }, [selectedAsset, startDate, endDate, selectedTimelineDate]);

  return (
    <Box
      sx={{
        width: '100%',
        height: 'calc(100vh - 56px)',
        overflowY: 'auto',
        overflowX: 'hidden',
        backgroundColor: palette.pageBackground,
        pb: 5,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {isLoading && <SingleFadeLoader message={t('loading_critical_assets')} />}
      <Box
        sx={{
          padding: '24px',
          flexGrow: 1,
          height: '100%',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <FatigueDateRangeDiv
            isSelected={isSelected}
            setIsSelected={setIsSelected}
            handleDateChange={handleDateChange}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            yesterday={dayjs().subtract(1, 'day').toDate()}
            aggregation={aggregation}
            setAggregation={setAggregation}
            handleFetchCustomDateRangeData={async () => {
              await fetchAssets(startDate, endDate, selectedAsset?.asset_id);
            }}
            handleFetchCustomOnAggregationChange={async ({
              startDate: s,
              endDate: e,
            }) => {
              await fetchAssets(s, e);
            }}
            isLoading={isLoading}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 600,
                color: palette.textSecondary,
              }}
            >
              {t('asset')}
            </Typography>
            <Autocomplete
              options={assets}
              getOptionLabel={(option: any) => option?.asset_id || ''}
              value={selectedAsset}
              onChange={(e, val) => {
                if (val) setSelectedAsset(val);
              }}
              disableClearable
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  sx={{
                    '& .MuiInputBase-root': {
                      height: '32px',
                      fontSize: '10px',
                      fontWeight: 600,
                      paddingTop: '0px',
                      paddingBottom: '0px',
                    },
                    '& .MuiOutlinedInput-root': {
                      color: palette.textPrimary,
                      '& fieldset': {
                        borderColor: enableDarkTheme
                          ? palette.borderGrey
                          : palette.border,
                      },
                      '&:hover fieldset': {
                        borderColor: palette.primary,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: palette.primary,
                      },
                    },
                    '& .MuiIconButton-root': {
                      color: palette.textSecondary,
                      padding: '4px',
                    },
                  }}
                />
              )}
              sx={{ width: 140 }}
            />
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexGrow: 1,
            overflow: 'hidden',
            minHeight: 0,
          }}
        >
          <AssetDetailDashboard
            selectedAsset={selectedAsset}
            detailData={detailData}
            enableDarkTheme={enableDarkTheme}
            selectedTimelineDate={selectedTimelineDate}
            onTimelineDateSelect={setSelectedTimelineDate}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default CriticalAssets;
